import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { MagacinProduct, MagacinProductType } from '../types';
import { magacinArrivalsAPI, magacinProductsAPI, magacinProductTypesAPI } from '../services/api';
import './Magacin.css';

const unitOptions = [
  { label: 'kg', value: 'kg' },
  { label: 'kom', value: 'kom' },
  { label: 'm', value: 'm' },
  { label: 'l', value: 'l' }
];

const classOptions = [
  { label: 'Klasa A', value: 'A' },
  { label: 'Klasa B', value: 'B' },
  { label: 'Klasa C', value: 'C' }
];

const toDate = (d: any) => (d instanceof Date ? d : d ? new Date(d) : new Date());

const MagacinProductCreate: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [productTypes, setProductTypes] = useState<MagacinProductType[]>([]);
  const [products, setProducts] = useState<MagacinProduct[]>([]);

  const [productName, setProductName] = useState('');
  const [suggestions, setSuggestions] = useState<MagacinProduct[]>([]);
  const [selectedExisting, setSelectedExisting] = useState<MagacinProduct | null>(null);

  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(0);
  const [unit, setUnit] = useState('kg');
  const [productClass, setProductClass] = useState<'A' | 'B' | 'C'>('A');
  const [arrivalDate, setArrivalDate] = useState<Date>(new Date());
  const [supplier, setSupplier] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [minStock, setMinStock] = useState(0);
  const [maxStock, setMaxStock] = useState(1000);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [types, prods] = await Promise.all([magacinProductTypesAPI.getAll(), magacinProductsAPI.getAll()]);
      setProductTypes(types);
      setProducts(prods);
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri učitavanju podataka',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const categoryOptions = useMemo(
    () => productTypes.map((t) => ({ label: t.name, value: t._id })),
    [productTypes]
  );

  const selectedType = useMemo(() => productTypes.find((t) => t._id === categoryId) || null, [productTypes, categoryId]);

  useEffect(() => {
    if (!selectedType) return;
    // auto-fill defaults based on selected category/type
    setUnit(selectedType.unit || 'kg');
    setProductClass(selectedType.productClass || 'A');
    setMinStock(selectedType.minStock ?? 0);
    setMaxStock(selectedType.maxStock ?? 1000);
  }, [selectedType?._id]);

  const complete = (e: AutoCompleteCompleteEvent) => {
    const q = (e.query || '').toLowerCase().trim();
    if (!q) {
      setSuggestions(products.slice(0, 10));
      return;
    }
    const filtered = products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 10);
    setSuggestions(filtered);
  };

  const onSelectExisting = (p: MagacinProduct) => {
    setSelectedExisting(p);
    setProductName(p.name);
    setCategoryId(null);
    setUnit(p.unit || 'kg');
    setProductClass((p.productClass as any) || 'A');
    setMinStock(p.minStock ?? 0);
    setMaxStock(p.maxStock ?? 1000);
    setBatchNumber(p.batchNumber || '');
  };

  const clearExistingIfEdited = (name: string) => {
    setProductName(name);
    if (selectedExisting && name !== selectedExisting.name) {
      setSelectedExisting(null);
    }
  };

  const canSubmit = useMemo(() => {
    const nameOk = productName.trim().length > 0;
    const qtyOk = quantity > 0;
    const supplierOk = supplier.trim().length > 0;
    const batchOk = batchNumber.trim().length > 0;
    const dateOk = !!arrivalDate;
    const rangeOk = maxStock >= minStock;

    if (!nameOk || !qtyOk || !supplierOk || !batchOk || !dateOk || !rangeOk) return false;
    if (!selectedExisting && !categoryId) return false; // new product requires category
    return true;
  }, [productName, quantity, supplier, batchNumber, arrivalDate, maxStock, minStock, selectedExisting, categoryId]);

  const submit = async () => {
    if (!canSubmit) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Popunite obavezna polja', life: 3000 });
      return;
    }

    setLoading(true);
    try {
      if (selectedExisting) {
        const fresh = await magacinProductsAPI.getById(selectedExisting._id);
        await magacinArrivalsAPI.create({
          productId: fresh._id,
          productName: fresh.name,
          productType: fresh.productType,
          quantity,
          unit: fresh.unit,
          arrivalDate: toDate(arrivalDate),
          supplier: supplier.trim(),
          batchNumber: batchNumber.trim(),
          notes: notes || ''
        } as any);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Dolazak je evidentiran', life: 2500 });
        navigate('/magacin/products');
        return;
      }

      const type = selectedType;
      if (!type) throw new Error('Kategorija nije izabrana');

      const created = await magacinProductsAPI.create({
        name: productName.trim(),
        productType: type.name,
        category: type.name,
        productClass,
        unit,
        description: description || '',
        minStock,
        maxStock,
        suppliers: supplier.trim() ? [supplier.trim()] : [],
        batchNumber: batchNumber.trim(),
        arrivalDate: toDate(arrivalDate),
        totalWeight: 0,
        consumedWeight: 0,
        availableWeight: 0
      } as any);

      await magacinArrivalsAPI.create({
        productId: created._id,
        productName: created.name,
        productType: created.productType,
        quantity,
        unit: created.unit,
        arrivalDate: toDate(arrivalDate),
        supplier: supplier.trim(),
        batchNumber: batchNumber.trim(),
        notes: notes || ''
      } as any);

      toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Proizvod je kreiran', life: 2500 });
      navigate('/magacin/products');
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || e?.message || 'Greška pri kreiranju',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="magacin-page p-4">
      <Toast ref={toast} />

      <div className="magacin-header flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Dodaj proizvod u magacin</h2>
          <div className="text-600 mt-1">Unesite podatke o dolasku proizvoda</div>
        </div>
        <div className="flex gap-2">
          <Button label="Nazad" icon="pi pi-arrow-left" severity="secondary" onClick={() => navigate('/magacin/products')} />
        </div>
      </div>

      <Card>
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-8">
            <label>Naziv proizvoda *</label>
            <AutoComplete
              value={productName}
              suggestions={suggestions}
              completeMethod={complete}
              field="name"
              dropdown
              forceSelection={false}
              placeholder="Unesite naziv proizvoda ili pretražite postojeće..."
              onChange={(e) => {
                const v: any = e.value;
                const name = typeof v === 'string' ? v : v?.name || '';
                clearExistingIfEdited(name);
              }}
              onSelect={(e) => onSelectExisting(e.value as MagacinProduct)}
            />
            {selectedExisting && (
              <small className="text-600">Izabran postojeći proizvod — evidencija dolaska će povećati stanje.</small>
            )}
          </div>

          <div className="field col-12 md:col-4">
            <label>Kategorija *</label>
            <Dropdown
              value={categoryId}
              options={categoryOptions}
              onChange={(e) => setCategoryId(e.value)}
              appendTo="self"
              placeholder="Odaberite kategoriju"
              disabled={!!selectedExisting}
              filter
              showClear
            />
          </div>

          <div className="field col-12 md:col-6">
            <label>Količina *</label>
            <InputNumber value={quantity} onValueChange={(e) => setQuantity(e.value ?? 0)} min={0} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Jedinica mere</label>
            <Dropdown
              value={unit}
              options={unitOptions}
              onChange={(e) => setUnit(e.value)}
              appendTo="self"
              disabled={!!selectedExisting}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label>Klasa proizvoda</label>
            <Dropdown
              value={productClass}
              options={classOptions}
              onChange={(e) => setProductClass(e.value)}
              appendTo="self"
              disabled={!!selectedExisting}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Datum dolaska *</label>
            <Calendar value={arrivalDate} onChange={(e) => setArrivalDate(toDate(e.value))} dateFormat="dd.mm.yy" showIcon />
          </div>

          <div className="field col-12 md:col-6">
            <label>Dobavljač *</label>
            <InputText value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Naziv dobavljača" />
          </div>
          <div className="field col-12 md:col-6">
            <label>Broj serije *</label>
            <InputText value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} placeholder="Broj serije proizvoda" />
          </div>

          <div className="field col-12 md:col-6">
            <label>Minimalno stanje</label>
            <InputNumber value={minStock} onValueChange={(e) => setMinStock(e.value ?? 0)} min={0} disabled={!!selectedExisting} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Maksimalno stanje</label>
            <InputNumber value={maxStock} onValueChange={(e) => setMaxStock(e.value ?? 0)} min={0} disabled={!!selectedExisting} />
          </div>

          <div className="field col-12">
            <label>Opis proizvoda</label>
            <InputTextarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} disabled={!!selectedExisting} />
          </div>
          <div className="field col-12">
            <label>Napomene</label>
            <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
          </div>
        </div>

        <div className="flex justify-content-end gap-2 mt-3">
          <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => navigate('/magacin/products')} />
          <Button label="Kreiraj proizvod" icon="pi pi-check" severity="info" onClick={submit} loading={loading} disabled={!canSubmit} />
        </div>
      </Card>
    </div>
  );
};

export default MagacinProductCreate;


